import {
  createSlice,
  createEntityAdapter,
  createSelector,
  createAsyncThunk,
  // PayloadAction,
  // createAction,
} from "@reduxjs/toolkit";
import { normalize } from "normalizr";
import { HYDRATE } from "next-redux-wrapper";
import { createWrapper } from "admin/api/axiosWrapper";
import { ErrorResponse } from "admin/types/errorResponse";
import {
  isFulfilledAction,
  isPendingAction,
  isRejectedAction,
} from "admin/utils/sliceUtil";
import { Hotel } from "admin/types/hotel";
import { RootState } from "admin/store";
import {
  API_BASE_PATH,
  COMPANY_ADMIN_API_VER,
} from "admin/constants/constants";
import { Meta } from "admin/types/meta";
import { PageContext } from "admin/types/pageContext";
import { AdminTypes } from "admin/constants/adminTypes";
import { objectToFormData } from "admin/utils/objectUtil";
import { hotelSchema } from "admin/schemas/hotelSchema";
import { ChannelManagerTypes } from "admin/constants/channelManagerTypes";
import { HotelBillingTypes } from "../../constants/hotelBillingTypes";
import { HotelFacilityTypes } from "admin/constants/hotelFacilityTypes";
import { HotelTranslationTypes } from "admin/constants/hotelTranslationTypes";

const key = "hotel";

const adapter = createEntityAdapter<Hotel.Hotel, string>({
  selectId: (hotel) => hotel.code,
});
const managerHotelAdapter = createEntityAdapter<Hotel.Hotel, string>({
  selectId: (hotel) => hotel.code,
});

const managerHotelInitialStateValues: Hotel.ManagerHotelState = {
  meta: null,
};
const managerHotelInitialState = managerHotelAdapter.getInitialState(
  managerHotelInitialStateValues,
);

const initialStateValues: Hotel.HotelState = {
  hotelId: null,
  errors: null,
  loading: false,
  meta: null,
  managerHotels: managerHotelInitialState,
};
const initialState = adapter.getInitialState(initialStateValues);

/** Async **/
export const fetchManagerHotels = createAsyncThunk<
  {
    hotels: Hotel.Hotel[];
    meta: Meta;
  },
  {
    param?: string;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchManagerHotels`, async (args, { rejectWithValue }) => {
  try {
    const { param, isServerSide = false, headers, locale } = args;
    const url = `${API_BASE_PATH}/manager/hotels.json?${param}`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      hotels: result.data.hotels,
      meta: result.data.meta,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});
export const fetchHotels = createAsyncThunk<
  {
    hotels: Hotel.Hotel[];
    meta: Meta;
  },
  {
    context: PageContext.PageContext;
    param?: string;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchHotels`, async (args, { rejectWithValue }) => {
  try {
    const { context, param, isServerSide = false, headers, locale } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/hotels.json?${param}`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels.json?${param}`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      hotels: result.data.hotels,
      meta: result.data.meta,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

const fetchHotelData = async ({
  context,
  hotelId,
  isServerSide = false,
  headers,
  locale,
}: {
  context: PageContext.PageContext;
  hotelId: string;
  isServerSide?: boolean;
  headers?: any;
  locale?: string;
}) => {
  const url =
    context.adminType === AdminTypes.manager
      ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/hotels/${hotelId}.json`
      : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}.json`;

  const result = await createWrapper({ isServerSide, locale }).get(url, {
    headers,
  });

  return normalize(result.data.hotel, hotelSchema);
};

export const fetchHotel = createAsyncThunk<
  any,
  {
    context: PageContext.PageContext;
    hotelId: string;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchHotel`, async (args, { rejectWithValue }) => {
  try {
    const normalized = await fetchHotelData(args);
    // console.log(normalized);
    return normalized;
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const fetchHotelIfNeeded = createAsyncThunk<
  any,
  {
    context: PageContext.PageContext;
    hotelId: string;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
    state: RootState;
  }
>(
  `${key}/fetchHotelIfNeeded`,
  async (args, { rejectWithValue }) => {
    try {
      const normalized = await fetchHotelData(args);
      return normalized;
    } catch (err) {
      return rejectWithValue(err.data.errors);
    }
  },
  {
    condition: (args, { getState }) => {
      const { hotelId } = args;
      const { hotel } = getState();
      const selectedHotelId = hotel.hotelId;
      // データが存在する場合はアクションをスキップ
      return !(selectedHotelId === hotelId);
    },
  },
);

export const postHotel = createAsyncThunk<
  any,
  { context: PageContext.PageContext; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postHotel`, async (args, { rejectWithValue }) => {
  try {
    const { context, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/hotels.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels.json`;
    const formData: any = new FormData();
    objectToFormData(params, formData, "hotel");
    const result = await createWrapper({ isMultipart: true }).post(
      url,
      formData,
    );
    // console.log(result);
    const normalized = normalize(result.data.hotel, hotelSchema);
    return normalized;
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const putHotel = createAsyncThunk<
  any,
  { context: PageContext.PageContext; hotelId: string; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putHotel`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelId, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/hotels/${hotelId}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}.json`;
    const formData: any = new FormData();
    objectToFormData(params, formData, "hotel");
    const result = await createWrapper({ isMultipart: true }).put(
      url,
      formData,
    );
    // console.log(result);
    const normalized = normalize(result.data.hotel, hotelSchema);
    return normalized;
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postHotelMailSettings = createAsyncThunk<
  any,
  { context: PageContext.PageContext; hotelId: string; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postHotelMailSettings`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelId, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/mail_settings.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/mail_settings.json`;
    const result = await createWrapper().post(url, { hotel: params });
    // console.log(result);
    const normalized = normalize(result.data.hotel, hotelSchema);
    return normalized;
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const importHotel = createAsyncThunk<
  any,
  { context: PageContext.PageContext; hotelId: string },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/importHotel`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelId } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/import`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/import`;
    await createWrapper().post(url, {});
    return {};
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postHotelChannelManager = createAsyncThunk<
  any,
  { hotelId: string; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postHotelChannelManager`, async (args, { rejectWithValue }) => {
  try {
    const { hotelId, params } = args;
    const url = `${API_BASE_PATH}/manager/hotels/${hotelId}/channel_managers`;
    const result = await createWrapper().post(url, { hotel: params });
    return normalize(result.data.hotel, hotelSchema);
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postCheckInSettings = createAsyncThunk<
  any,
  { context: PageContext.PageContext; hotelId: string; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postCheckInSettings`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelId, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/check_in_settings.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/check_in_settings.json`;
    const result = await createWrapper().post(url, { hotel: params });
    // console.log(result);
    const normalized = normalize(result.data.hotel, hotelSchema);
    return normalized;
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const requestGeocodingSuggestions = createAsyncThunk<
  {
    latitude: number;
    longitude: number;
  },
  { context: PageContext.PageContext; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/requestGeocodingSuggestions`, async (args, { rejectWithValue }) => {
  try {
    const { context, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/geocoding_suggestions.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/geocoding_suggestions.json`;
    const result = await createWrapper().post(url, params);
    return result.data;
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

/** slice **/
export const hotelSlice = createSlice({
  name: key,
  initialState,
  reducers: {
    clearSelectedHotel: (state) => {
      state.hotelId = null;
    },
    resetHotelState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        return { ...state, ...action.payload.hotel };
      })
      .addCase(fetchManagerHotels.fulfilled, (state, action) => {
        managerHotelAdapter.setAll(state.managerHotels, action.payload.hotels);
        state.managerHotels.meta = action.payload.meta;
      })
      .addCase(fetchHotels.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.hotels);
        state.meta = action.payload.meta;
      })
      .addCase(fetchHotel.pending, (state, action) => {
        const targetHotelId = action.meta.arg.hotelId;
        if (state.hotelId && state.hotelId !== targetHotelId) {
          // 別のホテルデータを取得するため選択中のホテルをクリアする
          state.hotelId = null;
        }
      })
      .addCase(fetchHotel.fulfilled, (state, action) => {
        adapter.upsertMany(state, action.payload.entities.hotel);
        state.hotelId = action.payload.result;
      })
      .addCase(fetchHotelIfNeeded.pending, (state) => {
        // 常に別のホテルデータを取得するため選択中のホテルをクリアする
        state.hotelId = null;
      })
      .addCase(fetchHotelIfNeeded.fulfilled, (state, action) => {
        adapter.upsertMany(state, action.payload.entities.hotel);
        state.hotelId = action.payload.result;
      })
      .addCase(postHotel.fulfilled, (state, action) => {
        adapter.upsertMany(state, action.payload.entities.hotel);
      })
      .addCase(putHotel.fulfilled, (state, action) => {
        adapter.upsertMany(state, action.payload.entities.hotel);
      })
      .addCase(postHotelMailSettings.fulfilled, (state, action) => {
        adapter.upsertMany(state, action.payload.entities.hotel);
      })
      .addCase(postHotelChannelManager.fulfilled, (state, action) => {
        adapter.upsertMany(state, action.payload.entities.hotel);
      })
      .addCase(postCheckInSettings.fulfilled, (state, action) => {
        adapter.upsertMany(state, action.payload.entities.hotel);
      })
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action: any) => {
        state.errors = action.payload;
        state.loading = false;
      });
  },
});

/** selector **/
export const {
  selectById: hotelByIdSelector,
  selectIds: hotelIdsSelector,
  selectEntities: hotelEntitiesSelector,
  selectAll: hotelsSelector,
  selectTotal: totalHotelsSelector,
} = adapter.getSelectors((state: RootState) => state.hotel);
export const {
  selectById: managerHotelByIdSelector,
  selectIds: managerHotelIdsSelector,
  selectEntities: managerHotelEntitiesSelector,
  selectAll: managerHotelsSelector,
  selectTotal: totalManagerHotelsSelector,
} = managerHotelAdapter.getSelectors(
  (state: RootState) => state.hotel.managerHotels,
);

const stateSelector = (state: RootState) => state[key];

export const hotelSelector = createSelector(
  stateSelector,
  (state) => state.entities[state.hotelId],
);

export const errorsSelector = createSelector(
  stateSelector,
  (state) => state.errors,
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.loading,
);

export const hotelsMetaSelector = createSelector(
  stateSelector,
  (state) => state.meta,
);

export const managerHotelsMetaSelector = createSelector(
  stateSelector,
  (state) => state.managerHotels.meta,
);

export const hotelTranslationTypeSelector = createSelector(
  hotelSelector,
  (hotel) => {
    switch (hotel?.facilityType) {
      case HotelFacilityTypes.activities:
        return HotelTranslationTypes.activity;
      case HotelFacilityTypes.camping:
        return HotelTranslationTypes.camp;
      default:
        return HotelTranslationTypes.hotel;
    }
  },
);

/** action export **/
export const { clearSelectedHotel, resetHotelState } = hotelSlice.actions;

export const convertHotelFormValuesToParams = (data: any) => {
  const params = { ...data };
  if (params.logo_image_url) {
    delete params.logo_image;
  } else if (!params.logo_image) {
    params.logo_image = null;
  } else {
    params.logo_image = params.logo_image[0];
  }
  if (params.supported_language_single) {
    delete params.supported_language_single;
  }
  if (!canSetBasicFee(params.channel_manager_type)) {
    delete params.basic_fee_cents;
  }
  const credit_card_payment_required = params.credit_card_payment_required;
  params.supported_languages.map((lang) => {
    if (
      credit_card_payment_required &&
      credit_card_payment_required.includes(lang)
    ) {
      params[`credit_card_payment_required_${lang}`] = true;
    } else {
      params[`credit_card_payment_required_${lang}`] = false;
    }
  });

  if (!params.bookable_start_time_from_checkin) {
    params.bookable_start_time_from_checkin = "";
  }

  return params;
};

export const convertHotelChannelManagerFormValuesToParams = (data: any) => {
  const params = { ...data };
  const channelManagerType = params.channel_manager_type;
  if (!isChannelManagerCodeRequired(channelManagerType)) {
    delete params.channel_manager_code;
  }
  if (!isChannelManagerUsernameRequired(channelManagerType)) {
    delete params.channel_manager_username;
  }
  if (!isChannelManagerPasswordRequired(channelManagerType)) {
    delete params.channel_manager_password;
  }
  return params;
};

const channelManagerCodeRequired = {
  [ChannelManagerTypes.tlLincoln]: true,
  [ChannelManagerTypes.temairazu]: true,
  [ChannelManagerTypes.neppan]: true,
  [ChannelManagerTypes.raku2]: true,
};
const channelManagerUsernameRequired = {
  [ChannelManagerTypes.tlLincoln]: true,
};
const channelManagerPasswordRequired = {
  [ChannelManagerTypes.tlLincoln]: true,
  [ChannelManagerTypes.temairazu]: true,
  [ChannelManagerTypes.neppan]: true,
};
const basicFeeEnabled = {
  [ChannelManagerTypes.noCollaboration]: true,
};

export const isChannelManagerCodeRequired = (channelManagerType) => {
  return channelManagerCodeRequired[channelManagerType];
};
export const isChannelManagerUsernameRequired = (channelManagerType) => {
  return channelManagerUsernameRequired[channelManagerType];
};
export const isChannelManagerPasswordRequired = (channelManagerType) => {
  return channelManagerPasswordRequired[channelManagerType];
};
export const canSetBasicFee = (channelManagerType) => {
  return basicFeeEnabled[channelManagerType];
};

export const updatableChannelManagerTypes = [
  ChannelManagerTypes.neppan,
  ChannelManagerTypes.temairazu,
  ChannelManagerTypes.noCollaboration,
];

export const convertHotelToFormValues = (data: any) => {
  const values = { ...data };
  values.calendar_cache_enabled = String(values.calendar_cache_enabled);
  values.enables_calendar_abbreviated_units = String(
    values.enables_calendar_abbreviated_units,
  );
  values.one_room_limited = String(values.one_room_limited);
  values.email_verification_required = String(
    values.email_verification_required,
  );
  let supported_languages = values.supported_languages;
  if (values.billing_type === HotelBillingTypes.free) {
    values.supported_language_single =
      values.supported_languages.length > 0
        ? values.supported_languages[0]
        : null;
    supported_languages = [values.supported_language_single];
  }
  values.credit_card_payment_required = [];
  values.force_onsite_payment = String(values.force_onsite_payment);
  supported_languages.map((langauge) => {
    if (values[`credit_card_payment_required_${langauge}`]) {
      values.credit_card_payment_required.push(langauge);
    }
  });
  return values;
};

export const canWriteHotelPlanCancelPolicy = (abilities: any) => {
  if (!abilities) {
    return false;
  }
  const canWrite = abilities["hotelPlan"]?.["writeHotelPlanCancelPolicy"];
  return !!canWrite;
};

export const canCreateField = (type: string, name: string, abilities: any) => {
  if (!abilities) {
    return true;
  }
  const cannotCreates = abilities[type]?.["cannotCreate"];
  if (!cannotCreates) {
    return true;
  }
  return !cannotCreates.includes(name);
};

export const canUpdateField = (type: string, name: string, abilities: any) => {
  if (!abilities) {
    return true;
  }
  const cannotUpdates = abilities[type]?.["cannotUpdate"];
  if (!cannotUpdates) {
    return true;
  }
  return !cannotUpdates.includes(name);
};

export const canReadField = (type: string, name: string, abilities: any) => {
  if (!abilities) {
    return true;
  }
  const cannotRead = abilities[type]?.["cannotRead"];
  if (!cannotRead) {
    return true;
  }
  return !cannotRead.includes(name);
};

export const canAbility = (
  abilities: any,
  type: string,
  action: string,
): boolean => {
  return abilities[type]?.[action] ?? false;
};

export const redirectPropsIfNoAbilities = (
  hotelResult: {
    entities?: any;
    errorCode?: string;
  },
  resources: string[],
  hotelId?: string,
) => {
  const hotel = hotelResult?.entities?.hotel?.[hotelId];
  if (!hotelResult || hotelResult.errorCode === "exception" || !hotel) {
    return {
      redirect: {
        destination: `/admin/signin`,
        permanent: false,
      },
    };
  }

  let hasAbility = hotel.billingAbilities;
  let redirectProps = null;
  resources.some((resource: string) => {
    hasAbility = hasAbility[resource];
    if (!hasAbility) {
      redirectProps = {
        redirect: {
          destination: `/admin/errors/access_denied`,
          permanent: false,
        },
      };
      return redirectProps;
    }
  });
  if (redirectProps) {
    return redirectProps;
  }
};

export const canChangePriceSyncable = (hotel: Hotel.Hotel) => {
  const excludeTypes = [
    ChannelManagerTypes.tlLincoln,
    ChannelManagerTypes.noCollaboration,
    ChannelManagerTypes.raku2,
  ];
  return !excludeTypes.includes(hotel.channelManagerType);
};

export const redirectPathIfNoAbilities = (
  hotelResult: {
    entities?: any;
    errorCode?: string;
  },
  resources: string[],
  hotelId?: string,
  context?: PageContext.PageContext,
) => {
  const adminType = context?.adminType || AdminTypes.admin;
  const hotel = hotelResult?.entities?.hotel?.[hotelId];
  if (!hotelResult || hotelResult.errorCode === "exception" || !hotel) {
    return `/${adminType}/signin`;
  }

  let hasAbility = hotel.billingAbilities;
  let redirectPath = null;
  resources.some((resource: string) => {
    hasAbility = hasAbility[resource];
    if (!hasAbility) {
      redirectPath = `/${adminType}/errors/access_denied`;
      return redirectPath;
    }
  });
  if (redirectPath) {
    return redirectPath;
  }
};
